import React, { useEffect, useState } from 'react';

import { Accordion, Button, Card, Modal, Table, useAccordionButton } from 'react-bootstrap';
import { differenceInCalendarDays, parse } from 'date-fns';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import PersonalInformationForm from './PersonalInformationForm';
import CheckoutForm from './CheckoutForm';
import CustomToggle from './CustomToggle';
import useBookingData from '../../state/booking/hooks/useProducer';

const BUNDLE_VALIDATION = {
  valid: 'Valid',
  invalid: 'Invalid',
  pending: 'Pending',
};

function GoBackCheckOut({ eventKey }) {
  const decoratedOnClick = useAccordionButton(eventKey);

  return (
    <div className="d-grid gap-2 mx-4 mb-2">
      <Button variant="outline-dark" onClick={decoratedOnClick} className="text-light" size="sm">
        Back to Room Detail
      </Button>
    </div>
  );
}

const checkBundleId = async (bundleId) => {
  const res = await fetch(`${process.env.API_URL}/hotel/booking/pre-check/`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      booking_request_id: bundleId,
    }),
  });
  const resData = await res.json();
  return resData;
};

function HotelPayment({ show, handleClose, room, hotelId, paymentSuccess }) {
  const [personalInformation, setPersonalInformation] = useState({});
  const [validateBundleId, setValidateBundleId] = useState(BUNDLE_VALIDATION.pending);
  const { booking, initialData } = useBookingData();

  const rateData = room.rate_data;
  useEffect(() => {
    if (show) {
      checkBundleId(rateData.ppn_bundle)
        .then(() => {
          setValidateBundleId(BUNDLE_VALIDATION.valid);
        })
        .catch(() => {
          setValidateBundleId(BUNDLE_VALIDATION.invalid);
        });
    }
  }, [show]);

  let postMandatory = 0;
  let preMandatory = 0;

  if (Object.prototype.hasOwnProperty.call(rateData.price_details, 'mandatory_fee_details')) {
    const mandatoryFee = rateData.price_details.mandatory_fee_details;
    if (mandatoryFee && Object.prototype.hasOwnProperty.call(mandatoryFee, 'breakdown')) {
      postMandatory = mandatoryFee.breakdown.postpaid.display_total;
      preMandatory = mandatoryFee.breakdown.prepaid.display_total;
    }
  }
  const checkIn = parse(booking.check_in, 'MM/dd/yyyy', new Date());
  const checkOut = parse(booking.check_out, 'MM/dd/yyyy', new Date());
  const days = differenceInCalendarDays(checkOut, checkIn);

  const showNewRate = () => {
    handleClose();
    initialData();
  };
  const getPriceInfo = () => {
    if (rateData.benchmark_price_details.saving_percentage > 0) {
      return (
        <div className="d-flex justify-content-end flex-column float-end">
          <span className="text-decoration-line-through text-muted">${(Math.round(rateData.benchmark_price_details.display_price * 100) / 100).toFixed(2)}</span>
          <span className="fs-5 select-color">${(Math.round(rateData.price_details.display_price * 100) / 100).toFixed(2)}</span>
        </div>
      );
    }
    return (
      <div className="d-flex align-items-center justify-content-end">
        <span className="fs-5 select-color">${(Math.round(rateData.price_details.display_price * 100) / 100).toFixed(2)}</span>
      </div>
    );
  };

  const mandatoryTable = () => {
    if (!(postMandatory > 0 || preMandatory > 0)) {
      return '';
    }
    return (
      <>
        <tr className="fs-5">
          <th className="align-middle" colSpan={2}>
            Mandatory Fee
          </th>
        </tr>
        {postMandatory.toFixed(2) > 0 && (
          <tr>
            <td className="align-middle">Due at the hotel</td>
            <td className="text-end">${postMandatory.toFixed(2)}</td>
          </tr>
        )}
        {preMandatory.toFixed(2) > 0 && (
          <tr>
            <td className="align-middle">Prepaid</td>
            <td className="text-end">${preMandatory.toFixed(2)}</td>
          </tr>
        )}
      </>
    );
  };

  const paymentTable = () => {
    return (
      <Accordion defaultActiveKey={['0']} flush>
        <Card className="my-2 bg-black">
          <Card.Header>Price Details</Card.Header>
          <Accordion.Collapse eventKey="0">
            <Card.Body>
              <Table bordered variant="black">
                <tbody>
                  <tr>
                    <td className="align-middle">Per Night</td>
                    <td className="text-end">{getPriceInfo()}</td>
                  </tr>
                  <tr>
                    <td className="align-middle">
                      {days} day(s) x {booking.rooms} room(s)
                    </td>
                    <td className="text-end">${rateData.price_details.display_sub_total.toFixed(2)}</td>
                  </tr>
                  <tr>
                    <td className="align-middle">Taxes and Fees</td>
                    <td className="text-end">${rateData.price_details.display_taxes.toFixed(2)}</td>
                  </tr>
                  {mandatoryTable()}
                  <tr className="fs-5">
                    <th className="align-middle">Total Trip Price</th>
                    <th className="text-end">${rateData.price_details.display_all_in_total.toFixed(2)}</th>
                  </tr>
                  <tr className="fs-6">
                    <th className="align-middle">What you pay now</th>
                    <th className="text-end">${rateData.price_details.display_total.toFixed(2)}</th>
                  </tr>
                </tbody>
              </Table>
              <CustomToggle eventKey="1">Book Now</CustomToggle>
            </Card.Body>
          </Accordion.Collapse>
        </Card>
        <Card className="my-2 bg-black">
          <Card.Header>Guest Information</Card.Header>
          <Accordion.Collapse appear eventKey="1">
            <>
              <PersonalInformationForm
                personalInformation={personalInformation}
                setData={(data) => {
                  setPersonalInformation(data);
                }}
              />
              <GoBackCheckOut eventKey="0" />
            </>
          </Accordion.Collapse>
        </Card>
        <Card className="my-2 bg-black">
          <Card.Header>Payment Details</Card.Header>
          <Accordion.Collapse appear eventKey="2">
            <>
              <CheckoutForm rateData={rateData} checkIn={checkIn} checkOut={checkOut} roomId={room.id} hotelId={hotelId} personalInformation={personalInformation} paymentSuccess={paymentSuccess} />
              <GoBackCheckOut eventKey="0" />
            </>
          </Accordion.Collapse>
        </Card>
      </Accordion>
    );
  };

  const invalidBundle = () => {
    return (
      <Card className="my-2 bg-black text-center">
        <Card.Body>
          <Card.Title>Room rates have changed, you can continue with your booking by viewing the new rates.</Card.Title>
          <Button variant="primary" className="btn-select" style={{ display: 'inline-block' }} onClick={() => showNewRate()}>
            Show New Rates
          </Button>
        </Card.Body>
      </Card>
    );
  };

  const render = () => {
    if (validateBundleId === BUNDLE_VALIDATION.valid) {
      return paymentTable();
    }
    if (validateBundleId === BUNDLE_VALIDATION.invalid) {
      return invalidBundle();
    }
    return (
      <SkeletonTheme baseColor="#202020" highlightColor="#444">
        <Skeleton count={3} />
      </SkeletonTheme>
    );
  };

  return (
    <>
      <Modal show={show} onHide={handleClose} backdrop="static" keyboard={false} size="lg">
        <Modal.Header closeButton className="bg-dark" closeVariant="white">
          <Modal.Title>{room.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-dark">{render()}</Modal.Body>
      </Modal>
    </>
  );
}

export default HotelPayment;
